import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  Stack,
  InputLabel,
  Select,
  OutlinedInput,
  Chip,
  Box,
} from '@mui/material';
import { useState } from 'react';
import { PortalRole, useCreateUserMutation } from 'src/backend';
import LoadingButton from 'src/components/LoadingButton';

export type CreateUserRequest = {
  name: string;
  email: string;
  role: string;
  customerIds?: string[];
};

export type Customer = {
  id: string;
  name: string;
};

interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  onUserCreated: () => void;
  customers: Customer[];
}

function CreateUserDialog({
  open,
  onClose,
  onUserCreated,
  customers,
}: CreateUserDialogProps) {
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: PortalRole.User,
    customerIds: [] as string[],
  });

  const [createUser, { isLoading: isCreatingUser }] = useCreateUserMutation();

  const getPortalRoleOptions = () => [
    { value: PortalRole.User, label: 'User' },
    { value: PortalRole.Admin, label: 'Admin' },
  ];

  const handleAddUser = () => {
    const request: CreateUserRequest = {
      name: newUser.name,
      email: newUser.email,
      role: PortalRole[newUser.role],
      // Only include customerIds for non-Admin users
      customerIds:
        newUser.role === PortalRole.Admin ? undefined : newUser.customerIds,
    };

    createUser(request)
      .unwrap()
      .then(() => {
        onUserCreated();
        onClose();
        setNewUser({
          name: '',
          email: '',
          role: PortalRole.User,
          customerIds: [],
        });
      });
  };

  const handleClose = () => {
    onClose();
    setNewUser({
      name: '',
      email: '',
      role: PortalRole.User,
      customerIds: [],
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ padding: '2rem', fontSize: '2.5rem' }}>
        Add New User
      </DialogTitle>
      <DialogContent sx={{ padding: '2rem' }}>
        <Stack spacing={3}>
          <TextField
            label="Name"
            value={newUser.name}
            onChange={(e) =>
              setNewUser((prev) => ({ ...prev, name: e.target.value }))
            }
            fullWidth
            required
          />
          <TextField
            label="Email"
            type="email"
            value={newUser.email}
            onChange={(e) =>
              setNewUser((prev) => ({ ...prev, email: e.target.value }))
            }
            fullWidth
            required
          />
          <FormControl fullWidth>
            <TextField
              select
              label="Role"
              value={newUser.role}
              onChange={(e) =>
                setNewUser((prev) => ({
                  ...prev,
                  role: Number(e.target.value) as PortalRole,
                  // Clear customerIds when Admin is selected
                  customerIds:
                    Number(e.target.value) === PortalRole.Admin
                      ? []
                      : prev.customerIds,
                }))
              }
            >
              {getPortalRoleOptions().map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="customers-label">Customers</InputLabel>
            <Select
              labelId="customers-label"
              multiple
              value={
                newUser.role === PortalRole.Admin ? [] : newUser.customerIds
              }
              disabled={newUser.role === PortalRole.Admin}
              onChange={(e) =>
                setNewUser((prev) => ({
                  ...prev,
                  customerIds:
                    typeof e.target.value === 'string'
                      ? e.target.value.split(',')
                      : e.target.value,
                }))
              }
              input={<OutlinedInput label="Customers" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      label={
                        customers.find((c) => c.id === value)?.name || value
                      }
                      size="small"
                    />
                  ))}
                </Box>
              )}
            >
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.name}
                </MenuItem>
              ))}
            </Select>
            {newUser.role === PortalRole.Admin && (
              <Box
                sx={{
                  mt: 1,
                  color: 'text.secondary',
                }}
              >
                Admin users have access to all customers automatically
              </Box>
            )}
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <LoadingButton
          loading={isCreatingUser}
          variant="contained"
          onClick={handleAddUser}
          disabled={!newUser.name || !newUser.email}
        >
          Add User
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default CreateUserDialog;
